import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {Master} from '../../../../admin/models/master.interface';
import {CdkDragDrop, copyArrayItem, moveItemInArray} from '@angular/cdk/drag-drop';
import {UnuKeyMapping} from '../../../models/unu-key-mapping.interface';
import {KfConfigMappings} from '../../../models/kf-config-mapping.interface';
import {GenericDialogComponent} from '../../../../shared/components/generic-dialog/generic-dialog.component';
import {MatDialog} from '@angular/material/dialog';

@Component({
  selector: 'app-kf-configuration-edit-drag-drop',
  templateUrl: './kf-configuration-edit-drag-drop.component.html',
  styleUrls: ['./kf-configuration-edit-drag-drop.component.scss']
})
export class KfConfigurationEditDragDropComponent implements OnChanges {

  @Input()
  unuKeyList!: Master[];
  @Input()
  dropBoxes!: Master[];
  @Input()
  titleDropBoxes!: string;
  @Input()
  initialMapping!: KfConfigMappings;
  @Input()
  initialMappingBeforeChanges!: KfConfigMappings;
  @Input()
  lifeCycleStatus!: string;

  @Output()
  previousStep: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  nextStep: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  loadItems: EventEmitter<void> = new EventEmitter();
  @Output()
  saveMappings: EventEmitter<UnuKeyMapping[]> = new EventEmitter<UnuKeyMapping[]>();

  list!: Master[];
  mappings: Master[][] = [];
  unallocatedSets!: number;

  constructor(private dialog: MatDialog) {
  }

  ngOnChanges(): void {
    this.list = [...this.unuKeyList];
    if (this.initialMapping) {
      let initialList = [...this.unuKeyList];
      const initialMappings: Master[][] = [];
      if (this.initialMapping.notInScopeUnuKeys?.length) {
        initialMappings.push(this.initialMapping.notInScopeUnuKeys);
        const notInScopeIds = this.initialMapping.notInScopeUnuKeys.map(u => u.id);
        initialList = initialList.filter(unuKey => !notInScopeIds.includes(unuKey.id));
      } else {
        initialMappings.push([]);
      }
      this.dropBoxes.forEach((item) => {
        this.initialMapping.unuKeyMappings.forEach(mapping => {
            if (item.id === mapping.schemaDetailElementId) {
              const mappedUnuKeys = this.list.filter(unuKey => mapping.unuKeyIds.includes(unuKey.id));
              initialMappings.push(mappedUnuKeys);
              initialList = initialList.filter(unuKey => !mapping.unuKeyIds.includes(unuKey.id));
            }
          }
        );
      });
      if (this.lifeCycleStatus === 'LAUNCHED' && initialMappings[0].length === 0) {
        initialMappings[0] = initialList; // Si el estado es LAUNCHED y hay unu keys no mapeadas las añadimos al not in scope
        this.list = [];
      } else {
        this.list = initialList;
      }
      this.mappings = initialMappings;
    } else {
      if (this.initialMappingBeforeChanges) {
        let initialList = [...this.unuKeyList];
        const initialMappings: Master[][] = [];
        if (this.initialMappingBeforeChanges.notInScopeUnuKeys?.length) {
          initialMappings.push(this.initialMappingBeforeChanges.notInScopeUnuKeys);
          const notInScopeIds = this.initialMappingBeforeChanges.notInScopeUnuKeys.map(u => u.id);
          initialList = initialList.filter(unuKey => !notInScopeIds.includes(unuKey.id));
        } else {
          initialMappings.push([]);
        }
        this.dropBoxes.forEach((item) => {
          this.initialMappingBeforeChanges.unuKeyMappings.forEach(mapping => {
              if (item.id === mapping.schemaDetailElementId) {
                const mappedUnuKeys = this.list.filter(unuKey => mapping.unuKeyIds.includes(unuKey.id));
                initialMappings.push(mappedUnuKeys);
                initialList = initialList.filter(unuKey => !mapping.unuKeyIds.includes(unuKey.id));
              }
            }
          );
        });
        if (this.lifeCycleStatus === 'LAUNCHED' && initialMappings[0].length === 0) {
          initialMappings[0] = initialList; // Si el estado es LAUNCHED y hay unu keys no mapeadas las añadimos al not in scope
          this.list = [];
        } else {
          this.list = initialList;
        }
        this.mappings = initialMappings;
      } else {
        this.mappings = [];
        this.dropBoxes.forEach(() => {
          this.mappings.push([]);
        });
        this.unallocatedSets = this.mappings.filter(mapping => mapping.length === 0).length;
      }
    }
  }

  drop(event: CdkDragDrop<Master[], any>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container?.data, event.previousIndex, event.currentIndex);
    } else {
      copyArrayItem(event.previousContainer?.data,
        event.container?.data || [],
        event.previousIndex,
        event.currentIndex);

      event.previousContainer?.data.splice(event.previousIndex, 1);
    }
    event.container.data.forEach((element, index) => {
      if (element.id === 0) {
        event.container.data.splice(index, 1);
      }
    });
    this.unallocatedSets = this.mappings.filter(mapping => mapping.length === 0).length;
  }

  save(): void {
    const checkEmptySet = this.checkAllSetsHaveOneElement();
    if (this.list.length !== 0 || checkEmptySet) {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        data:
          {
            title: 'Confirm incomplete groups definition',
            text: checkEmptySet
              ? 'One or more of the boxes on the right column have been left empty. Do you want to proceed to the next step?'
              : 'There are unmapped unu-keys. Do you want to proceed to the next step?'
          }
      });
      dialogRef.componentInstance.confirm.subscribe((confirm) => {
        if (confirm) {
          this.saveUnuKeyMappings();
          this.nextStep.emit();
        }
        dialogRef.close();
      });
    } else {
      this.saveUnuKeyMappings();
      this.nextStep.emit();
    }

  }

  private saveUnuKeyMappings(): void {
    const unuKeyMappings = [] as UnuKeyMapping[];
    this.mappings.forEach((item, index) => {
      if (index < this.dropBoxes.length) {
        if (this.dropBoxes[index].id > 0) { // Ignore not in scope mappings
          const schemaDetailElementId = this.dropBoxes[index].id;
          const unuKeyIds = item.map(unuKey => unuKey.id);
          unuKeyMappings.push({schemaDetailElementId, unuKeyIds});
        } else {
          unuKeyMappings.push({schemaDetailElementId: null, unuKeyIds: item.map(unuKey => unuKey.id)});
        }
      }
    });
    this.saveMappings.emit(unuKeyMappings);
  }

  private checkAllSetsHaveOneElement(): boolean {
    if (this.mappings && this.dropBoxes) {
      return this.mappings.filter((mapping, index) => (index === 0 || mapping.length > 0)).length < this.dropBoxes.length;
    }
    return false;
  }

}
